import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Добавляет months месяцев к дате (учитывает переполнение месяца). */
export function addMonths(date: Date | string, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Форматирует дату для <input type="datetime-local"> в локальной временной зоне. */
export function toLocalDatetimeLocalString(date: Date | string): string {
  const d = new Date(date)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

/** Форматирует дату для <input type="datetime-local"> в указанной IANA таймзоне. */
export function toZonedDatetimeLocalString(date: Date | string, timeZone: string): string {
  const d = new Date(date)
  const trimmedTimeZone = (timeZone ?? '').trim()
  if (!trimmedTimeZone) {
    return toLocalDatetimeLocalString(d)
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmedTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d)
    const byType = new Map(parts.map((p) => [p.type, p.value]))
    const y = byType.get('year')
    const m = byType.get('month')
    const day = byType.get('day')
    const h = byType.get('hour')
    const min = byType.get('minute')
    if (!y || !m || !day || !h || !min) {
      return toLocalDatetimeLocalString(d)
    }
    return `${y}-${m}-${day}T${h}:${min}`
  } catch {
    return toLocalDatetimeLocalString(d)
  }
}

/**
 * Возвращает смещение IANA-зоны от UTC в миллисекундах на конкретный момент.
 * Положительное значение — зона восточнее UTC (например, GMT+3 => +3*3600*1000).
 */
export function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = dtf.formatToParts(date)
  const byType = new Map(parts.map((p) => [p.type, p.value]))
  const y = Number(byType.get('year'))
  const m = Number(byType.get('month'))
  const d = Number(byType.get('day'))
  let h = Number(byType.get('hour'))
  const min = Number(byType.get('minute'))
  const s = Number(byType.get('second'))
  // Intl может вернуть час "24" для полуночи — нормализуем к 0.
  if (h === 24) h = 0
  const asUTC = Date.UTC(y, m - 1, d, h, min, s)
  return asUTC - date.getTime()
}

/**
 * Трактует строку из <input type="datetime-local"> (без offset, например "2026-06-07T08:30")
 * как стенные часы в указанной IANA-таймзоне и возвращает реальный момент времени.
 *
 * Согласовано с бэкендом (luxon `DateTime.fromISO(value, { zone })`).
 * Возвращает null, если строка некорректна.
 */
export function zonedWallClockToInstant(value?: string, timeZone?: string): Date | null {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return null
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (!match) {
    const fallback = new Date(trimmed)
    return Number.isNaN(fallback.getTime()) ? null : fallback
  }
  const [, ys, ms, ds, hs, mins, ss] = match
  const y = Number(ys)
  const m = Number(ms)
  const d = Number(ds)
  const h = Number(hs)
  const min = Number(mins)
  const s = ss ? Number(ss) : 0
  const trimmedTz = (timeZone ?? '').trim()
  // Базовая догадка: трактуем компоненты как UTC, затем корректируем на смещение зоны.
  const guessUtc = Date.UTC(y, m - 1, d, h, min, s)
  if (!trimmedTz) {
    return new Date(guessUtc)
  }
  try {
    const offset = getTimeZoneOffsetMs(trimmedTz, new Date(guessUtc))
    return new Date(guessUtc - offset)
  } catch {
    return new Date(guessUtc)
  }
}

/**
 * Возвращает человеко-читаемый offset таймзоны на конкретную дату, например "GMT+3".
 * Полезно для пояснения, по какому времени отображаются даты.
 */
export function getTimeZoneOffsetLabel(timeZone: string, date: Date = new Date()): string {
  const trimmedTimeZone = (timeZone ?? '').trim()
  if (!trimmedTimeZone) return 'GMT'
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmedTimeZone,
      timeZoneName: 'shortOffset' as any,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value
    const cleaned = (tzPart || '').replace('UTC', 'GMT')
    if (cleaned) return cleaned
  } catch {
    // ignore
  }
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmedTimeZone,
      timeZoneName: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value
    const cleaned = (tzPart || '').replace('UTC', 'GMT')
    if (cleaned) return cleaned
  } catch {
    // ignore
  }
  return 'GMT'
}

export function pluralize(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100

  if (mod100 >= 11 && mod100 <= 19) {
    return many
  }
  if (mod10 === 1) {
    return one
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few
  }
  return many
}

export function getTimeRemaining(endDate: Date | string): string {
  const end = new Date(endDate).getTime()
  const now = Date.now()
  const diff = end - now

  if (diff <= 0) {
    return 'Завершен'
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  if (days > 0) {
    return `${days} ${pluralize(days, 'день', 'дня', 'дней')}`
  }
  if (hours > 0) {
    return `${hours} ${pluralize(hours, 'час', 'часа', 'часов')}`
  }
  
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  return `${minutes} ${pluralize(minutes, 'минута', 'минуты', 'минут')}`
}
